import React, { useState, useRef, useCallback } from 'react';
import { Globe2, FileCheck, Truck, ChevronDown } from 'lucide-react';
import './Services.css';

const Services = ({ t, isVisible }) => {
  const [openService, setOpenService] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const serviceRefs = useRef([]);
  const contentRefs = useRef([]);

  const services = [
    {
      icon: <Globe2 size={48} />,
      title: t.services.service1.title,
      description: t.services.service1.description,
      features: t.services.service1.features,
      image: 'https://images.pexels.com/photos/8542360/pexels-photo-8542360.jpeg'
    },
    {
      icon: <FileCheck size={48} />,
      title: t.services.service2.title,
      description: t.services.service2.description,
      features: t.services.service2.features,
      image: 'https://images.pexels.com/photos/7063739/pexels-photo-7063739.jpeg'
    },
    {
      icon: <Truck size={48} />,
      title: t.services.service3.title,
      description: t.services.service3.description,
      features: t.services.service3.features,
      image: 'https://images.unsplash.com/photo-1571086291540-b137111fa1c7'
    }
  ];

  const scrollToService = useCallback((index) => {
    const element = serviceRefs.current[index];
    if (!element) return;

    // Get the sticky header height + padding for visual comfort
    const headerOffset = 80;
    
    // Calculate absolute position
    const elementRect = element.getBoundingClientRect();
    const absoluteTop = window.pageYOffset + elementRect.top - headerOffset;

    // Smooth scroll
    window.scrollTo({
      top: Math.max(0, absoluteTop),
      behavior: 'smooth'
    });
  }, []);

  const toggleService = useCallback((index) => {
    if (isAnimating) return;
    
    const isOpening = openService !== index;
    const contentElement = contentRefs.current[index];
    
    setIsAnimating(true);
    setOpenService(isOpening ? index : null);
    
    if (isOpening && contentElement) {
      // Define the handler for transitionend
      const handleTransitionEnd = (e) => {
        // Only respond to max-height transition on this specific element
        if (e.propertyName === 'max-height' && e.target === contentElement) {
          contentElement.removeEventListener('transitionend', handleTransitionEnd);
          // Scroll after animation completes
          scrollToService(index);
          // Reset animating state after scroll
          setTimeout(() => setIsAnimating(false), 400);
        }
      };
      
      // Add listener for transition end
      contentElement.addEventListener('transitionend', handleTransitionEnd);
      
      // Fallback timeout in case transitionend doesn't fire
      setTimeout(() => {
        contentElement.removeEventListener('transitionend', handleTransitionEnd);
        scrollToService(index);
        setTimeout(() => setIsAnimating(false), 400);
      }, 900);
    } else {
      // If closing, just reset after animation
      setTimeout(() => setIsAnimating(false), 800);
    }
  }, [openService, isAnimating, scrollToService]);

  return (
    <section id="services" className={`services ${isVisible ? 'visible' : ''}`}>
      <div className="services-container">
        <div className="services-header">
          <h2 className="services-title">{t.services.title}</h2>
          <p className="services-subtitle">{t.services.subtitle}</p>
        </div>

        <div className="services-accordion">
          {services.map((service, index) => (
            <div 
              key={index} 
              ref={el => serviceRefs.current[index] = el}
              className={`service-accordion-item ${openService === index ? 'open' : ''}`} 
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <button 
                className="service-accordion-header" 
                onClick={() => toggleService(index)}
                disabled={isAnimating}
                aria-expanded={openService === index}
              >
                <div className="service-accordion-title-wrapper">
                  <div className={`service-icon ${index === 0 ? 'service-icon--globe' : ''}`}>{service.icon}</div>
                  <h3 className="service-title">{service.title}</h3>
                </div>
                <ChevronDown className={`service-accordion-icon ${openService === index ? 'rotated' : ''}`} size={24} />
              </button>
              
              <div 
                ref={el => contentRefs.current[index] = el}
                className={`service-accordion-content ${openService === index ? 'open' : ''}`}
              >
                <div className="service-accordion-inner">
                  <div className="service-image-wrapper">
                    <img src={service.image} alt={service.title} className="service-image" />
                    <div className="service-image-overlay"></div>
                  </div>
                  <div className="service-content">
                    <p className="service-description">{service.description}</p>
                    <ul className="service-features">
                      {service.features.map((feature, fIndex) => (
                        <li key={fIndex}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;